import { createApi } from "@reduxjs/toolkit/query/react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { serializeError } from "serialize-error";
import { supabase, supabaseBaseQuery } from "../../../supabase";
import { promptRunner } from "../../prompts/classes/promptRunnerInstance";
import { clearJobFeedbackFromCaches } from "../../judgements/api/judgementsApi";
import type { ExecutionJob } from "../../prompts/types/executionJobTypes";
import type { EvalRun, EvalRunJob } from "../types";
import { ExecutionUIStreamReducer } from "../../prompts/helpers/executionUIStreamReducer";

interface InitiateEvalRunPayload {
  run_id: string;
  eval_id: string;
  /** The run currently displayed; used as the clone source for locked columns. */
  source_run_id?: string;
  /** Eval prompt ids whose results should be cloned instead of regenerated. */
  locked_eval_prompt_ids?: string[];
}

interface InitiateEvalRunResponse {
  run_id: string;
  job_count: number;
  cloned_job_count?: number;
}

interface ScheduleEvalExecutionJobPayload {
  /** The eval run whose jobs cache should be patched optimistically */
  eval_run_id: string;
  /** The prompt_runs.id for this cell — required to re-key the job. */
  run_id: string;
  job_id: string;
  display_name: string | null;
  payload: ExecutionJob["payload"];
  created_by: string;
}

const evalRunnerApi = createApi({
  reducerPath: "api.EvalRunner",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["CurrentEvalRun", "EvalRuns", "EvalRunJobs"],
  endpoints: (builder) => ({
    getCurrentEvalRun: builder.query<EvalRun | null, { evalId: string }>({
      queryFn: async ({ evalId }) => {
        const { data, error } = await supabase
          .rpc("get_latest_eval_run", {
            p_eval_id: evalId,
          })
          .maybeSingle();

        if (error) return { error: serializeError(error) };

        return { data: data as EvalRun | null };
      },
      providesTags: (_, __, { evalId }) => [{ type: "CurrentEvalRun", id: evalId }],
    }),

    getEvalRuns: builder.query<EvalRun[], { evalId: string }>({
      queryFn: async ({ evalId }) => {
        const { data, error } = await supabase.rpc("get_eval_runs", {
          p_eval_id: evalId,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as unknown as EvalRun[] };
      },
      providesTags: (_, __, { evalId }) => [{ type: "EvalRuns", id: evalId }],
    }),

    initiateEvalRun: builder.mutation<InitiateEvalRunResponse, InitiateEvalRunPayload>({
      queryFn: async ({ run_id, eval_id, source_run_id, locked_eval_prompt_ids }) => {
        const { data, error } = await supabase.functions.invoke("initiate-eval-run", {
          method: "POST",
          body: { run_id, eval_id, source_run_id, locked_eval_prompt_ids },
        });

        if (error) {
          // https://supabase.com/docs/guides/functions/error-handling#client-side-error-handling
          if (error instanceof FunctionsHttpError) {
            const errorData = await error.context.json();
            return { error: serializeError(errorData) };
          } else {
            return { error: serializeError(error) };
          }
        }

        return { data };
      },

      onQueryStarted: async (payload, { dispatch }) => {
        // Clear any stale jobs immediately so the grid doesn't flash a previous
        // run while the edge function schedules the new matrix.
        await dispatch(
          evalRunnerApi.util.upsertQueryData("getEvalRunJobs", { evalRunId: payload.run_id }, []),
        );
      },

      invalidatesTags: (_, error, { eval_id, run_id }) =>
        error
          ? []
          : [
              { type: "CurrentEvalRun", id: eval_id },
              { type: "EvalRuns", id: eval_id },
              { type: "EvalRunJobs", id: run_id },
            ],
    }),
    getEvalRunJobs: builder.query<EvalRunJob[], { evalRunId: string | undefined }>({
      queryFn: async ({ evalRunId }) => {
        if (!evalRunId) return { data: [] };

        const { data, error } = await supabase.rpc("get_eval_run_jobs", {
          p_eval_run_id: evalRunId,
        });

        if (error) return { error: serializeError(error) };

        return { data: data as unknown as EvalRunJob[] };
      },

      async onCacheEntryAdded(_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        const uiStreamReducer = new ExecutionUIStreamReducer();

        try {
          await cacheDataLoaded;

          const applyUIStreamPatch = (
            executionId: string,
            patch: {
              status?: ExecutionJob["status"];
              resultPatch: Record<string, unknown>;
            },
          ) => {
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === executionId);
              if (!log) return;
              if (patch.status) log.status = patch.status;
              log.result = {
                ...log.result,
                ...patch.resultPatch,
              };
            });
          };

          promptRunner.onUIChunk((envelope) => {
            uiStreamReducer.handleChunk(envelope.executionId, envelope.chunk, (patch) => {
              applyUIStreamPatch(envelope.executionId, patch);
            });
          });

          promptRunner.onError((message) => {
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === message.executionId);

              if (!log) return;

              log.status = message.status;
              log.result = {
                ...log.result,
                error: {
                  ...log.result?.error,
                  code: message.error.code,
                  message: message.error.message,
                },
              };
            });
          });

          promptRunner.onCancel((message) => {
            uiStreamReducer.close(message.executionId, "canceled");
            updateCachedData((draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const log = draft.find((log) => log.id === message.executionId);

              if (!log) return;

              log.status = message.status;
            });
          });
        } catch (e) {
          console.error(e);
        }

        await cacheEntryRemoved;
      },

      providesTags: (_, __, { evalRunId }) => [{ type: "EvalRunJobs", id: evalRunId }],
    }),

    // Per-cell re-run. Passes run_id = prompt_run_id so schedule_llm_execution_job
    // preserves the job's link to its prompt_runs row via the upsert.
    scheduleEvalExecutionJob: builder.mutation<void, ScheduleEvalExecutionJobPayload>({
      queryFn: async ({ job_id, run_id, display_name, payload, created_by }) => {
        const { data, error } = await supabase.functions.invoke("schedule-execution-job", {
          method: "POST",
          body: { job_id, run_id, display_name, payload, created_by },
        });

        if (error) {
          if (error instanceof FunctionsHttpError) {
            const errorData = await error.context.json();
            return { error: serializeError(errorData) };
          } else {
            return { error: serializeError(error) };
          }
        }

        return { data };
      },

      onQueryStarted: async (payload, { dispatch, queryFulfilled, getState }) => {
        const jobPatch = dispatch(
          evalRunnerApi.util.updateQueryData(
            "getEvalRunJobs",
            { evalRunId: payload.eval_run_id },
            (draft) => {
              // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
              const job = draft.find((job) => job.id === payload.job_id);

              if (!job) return;

              job.status = "pending";
              job.created_at = new Date().toISOString();
              job.result = null;
            },
          ),
        );

        // Match the DB clear-on-rerun trigger.
        clearJobFeedbackFromCaches(dispatch, getState, payload.job_id);

        try {
          await queryFulfilled;
        } catch {
          jobPatch.undo();
        }
      },
    }),
  }),
});

const {
  useGetCurrentEvalRunQuery,
  useGetEvalRunsQuery,
  useInitiateEvalRunMutation,
  useGetEvalRunJobsQuery,
  useScheduleEvalExecutionJobMutation,
} = evalRunnerApi;

export {
  evalRunnerApi,
  useGetCurrentEvalRunQuery,
  useGetEvalRunJobsQuery,
  useGetEvalRunsQuery,
  useInitiateEvalRunMutation,
  useScheduleEvalExecutionJobMutation,
};
